import { generateUniqueId } from '@utils/generators.ts';

import type { Answer, ItemQuizQuestion } from '../api/creatorElements.api';
import type { ItemQuizQuestionWithLocalId } from './creatorElementsSlice';

export const ensureQuestionTemporaryLocalId = (question: ItemQuizQuestion): ItemQuizQuestionWithLocalId => ({
    ...question,
    temporaryLocalId: generateUniqueId(),
});

export const ensureQuestionsTemporaryLocalIds = (questions: ItemQuizQuestion[]): ItemQuizQuestionWithLocalId[] =>
    questions.map(ensureQuestionTemporaryLocalId);

const resolveImportedQuestionId = (importedQuestion: ItemQuizQuestion): number | null => {
    if (importedQuestion.id === null) {
        return null;
    }

    const importedQuestionId = Number(importedQuestion.id);
    return Number.isNaN(importedQuestionId) ? null : importedQuestionId;
};

const mergeImportedAnswersWithAdminIds = (existingAnswers: Answer[], importedAnswers: Answer[]): Answer[] =>
    importedAnswers.map((importedAnswer) => {
        const existingAnswer = existingAnswers.find(
            (answer) =>
                answer.adminAnswerId && answer.text.trim().toLowerCase() === importedAnswer.text.trim().toLowerCase(),
        );

        return {
            text: importedAnswer.text,
            isCorrect: importedAnswer.isCorrect,
            adminAnswerId: existingAnswer?.adminAnswerId ?? null,
        };
    });

export const mergeQuestionsFromCsvImport = (
    existingQuestions: ItemQuizQuestionWithLocalId[],
    importedQuestions: ItemQuizQuestion[],
): ItemQuizQuestionWithLocalId[] => {
    const mergedQuestions = [...existingQuestions];

    importedQuestions.forEach((importedQuestion) => {
        const importedQuestionId = resolveImportedQuestionId(importedQuestion);

        if (importedQuestionId !== null) {
            const existingIndex = mergedQuestions.findIndex(
                (question) => question.id !== null && Number(question.id) === importedQuestionId,
            );

            if (existingIndex !== -1) {
                const existingQuestion = mergedQuestions[existingIndex];
                mergedQuestions[existingIndex] = {
                    ...importedQuestion,
                    id: importedQuestionId,
                    adminQuestionId: existingQuestion.adminQuestionId,
                    quizAnswers: mergeImportedAnswersWithAdminIds(
                        existingQuestion.quizAnswers,
                        importedQuestion.quizAnswers,
                    ),
                    status: existingQuestion.status,
                    temporaryLocalId: existingQuestion.temporaryLocalId,
                };
                return;
            }
        }

        mergedQuestions.push(
            ensureQuestionTemporaryLocalId({
                ...importedQuestion,
                id: null,
            }),
        );
    });

    return mergedQuestions;
};
